// frontend/src/services/api.js

import axios from "axios";
import authService from "./auth";

// Use environment variable or fallback to localhost
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

// Check if cookie-based auth is enabled
const USE_COOKIES = process.env.REACT_APP_USE_COOKIES === "true";

// =============================================================================
// Public Pages - Don't attempt token refresh on these paths
// =============================================================================
const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/terms',
  '/privacy',
  '/cookies',
  '/'
];

/**
 * Check if current page is a public page (doesn't require auth)
 */
const isPublicPage = () => {
  const path = window.location.pathname;
  return PUBLIC_PATHS.some(publicPath =>
    path === publicPath || path.startsWith(publicPath + '/')
  );
};

// Track if user has been authenticated in this session
// This helps prevent refresh attempts when user was never logged in
let hasBeenAuthenticated = false;

/**
 * Mark user as authenticated (call after successful login)
 */
export const markAuthenticated = () => {
  hasBeenAuthenticated = true;
};

/**
 * Clear authenticated state (call after logout)
 */
export const clearAuthenticated = () => {
  hasBeenAuthenticated = false;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  // Include cookies in requests when cookie mode is enabled
  withCredentials: USE_COOKIES,
  // Configure param serializer for FastAPI compatibility
  // FastAPI expects repeated params for arrays: ?status=active&status=trial
  // Not array brackets: ?status[]=active&status[]=trial
  paramsSerializer: {
    serialize: (params) => {
      const parts = [];
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (Array.isArray(value)) {
          // Send array values as repeated parameters
          value.forEach((item) => {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
          });
        } else if (value !== null && value !== undefined) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });
      return parts.join('&');
    }
  }
});

// =============================================================================
// Token Refresh State Management
// =============================================================================

// Flag to track if a token refresh is in progress
let isRefreshing = false;

// Queue to hold requests while token is being refreshed
// Each item has { resolve, reject } functions to control the pending promise
let failedQueue = [];

/**
 * Process all queued requests after token refresh completes.
 * @param {Error|null} error - Error if refresh failed, null if succeeded
 * @param {string|null} token - New access token if refresh succeeded
 */
const processQueue = (error, token) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  // Clear the queue
  failedQueue = [];
};

// =============================================================================
// Request Interceptor - Add Authorization Header (localStorage mode only)
// =============================================================================

api.interceptors.request.use(
  (config) => {
    // In cookie mode, authentication is handled via httpOnly cookies
    // No need to add Authorization header
    if (!USE_COOKIES) {
      const token = authService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// =============================================================================
// Response Interceptor - Silent Token Refresh on 401
// =============================================================================

api.interceptors.response.use(
  // Success handler - pass through
  (response) => response,

  // Error handler - handle 401 with silent refresh, 429 with friendly message
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest?.url;

    // An ABORTED request (a superseded list request - usePagination aborts
    // it on purpose) is not a network failure: no warning, and the caller gets
    // the cancel itself back, which it recognises and ignores.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Handle specific error types
    if (status === 401) {
      // 401 Unauthorized - will attempt token refresh below
    } else if (!error.response) {
      // Network error - backend may be unavailable
      console.warn(`[API] Network error on ${url} - Backend may be unavailable`);
      return Promise.reject(error);
    } else if (status === 429) {
      // Rate limit exceeded - show user-friendly message
      console.warn(`[API] Rate limit exceeded on ${url}`);
      const isHebrew = document.documentElement.dir === 'rtl' ||
                       localStorage.getItem('language') === 'hebrew';
      const friendlyMessage = isHebrew
        ? 'יותר מדי בקשות, אנא המתן רגע ונסה שוב'
        : 'Too many requests, please wait a moment and try again';

      const rateLimitError = new Error(friendlyMessage);
      rateLimitError.response = error.response;
      rateLimitError.isRateLimitError = true;
      return Promise.reject(rateLimitError);
    } else if (status >= 500) {
      // Server error - log and reject
      console.error(`[API] Server error ${status} on ${url}`);
      return Promise.reject(error);
    }

    // Only handle 401 errors below - all other errors rejected above
    if (status !== 401) {
      return Promise.reject(error);
    }

    // =========================================================================
    // PUBLIC PAGE CHECK REMOVED
    //
    // AuthContext now checks auth on ALL pages (including public pages).
    // Homepage redirects authenticated users to dashboard.
    // If user is not authenticated on public page, 401 is handled by trying refresh.
    // If refresh fails, user stays on public page (not redirected).
    //
    // This allows:
    // 1. Logged-in users to be redirected from homepage to dashboard
    // 2. Token refresh to work when reopening browser at homepage
    // 3. Graceful handling when user is not logged in (refresh fails, stays on page)
    // =========================================================================

    // =========================================================================
    // REMOVED: hasBeenAuthenticated check for cookie mode
    //
    // BUG FIX: The previous logic prevented refresh attempts when browser reopened:
    // - hasBeenAuthenticated resets to false on page load (JavaScript variable)
    // - But user might have valid refresh token cookie (30 days)
    // - Should ALWAYS attempt refresh in cookie mode (except public pages above)
    // - If refresh token is invalid, /auth/refresh-secure will return 401
    //
    // In cookie mode: Let the refresh endpoint determine if token is valid
    // In localStorage mode: Keep original behavior (check below)
    // =========================================================================
    // LocalStorage mode: Redirect unauthenticated users to login
    if (!USE_COOKIES && !hasBeenAuthenticated) {
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // =========================================================================
    // Prevent infinite loop if /auth/refresh endpoint itself returns 401
    // =========================================================================
    if (url && (url.includes("/auth/refresh") || url.includes("/auth/refresh-secure"))) {
      // Refresh token is invalid - clear tokens and redirect to login
      console.warn("[API] Refresh endpoint returned 401 - refresh token invalid");

      if (!USE_COOKIES) {
        authService.removeTokens();
      }
      isRefreshing = false;
      processQueue(error, null);

      window.location.href = "/login";
      return Promise.reject(error);
    }

    // Don't try to refresh on login endpoint failures (wrong password, etc.)
    if (url && (url.includes("/auth/login") || url.includes("/auth/login-secure"))) {
      return Promise.reject(error);
    }

    // Prevent infinite retry loops - only retry each request once
    if (originalRequest._retry) {
      // Already retried - clear tokens and redirect
      console.warn("[API] Request already retried after refresh - failing");
      authService.removeTokens();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // Mark request for retry after token refresh
    originalRequest._retry = true;

    // =========================================================================
    // Queue requests if refresh is already in progress
    // =========================================================================
    if (isRefreshing) {
      // Queue this request - will be retried after current refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          // Refresh succeeded - retry original request with new token
          if (!USE_COOKIES && token && token !== "cookie") {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        })
        .catch((err) => {
          // Refresh failed - reject this request
          return Promise.reject(err);
        });
    }

    // =========================================================================
    // Start token refresh process
    // =========================================================================
    isRefreshing = true;

    return new Promise((resolve, reject) => {
      authService
        .refreshAccessToken()
        .then((newToken) => {
          if (!newToken) {
            // Refresh failed - no new token returned
            // Only log on protected pages (expected on public pages for non-logged-in users)
            if (!isPublicPage()) {
              console.warn("[API] Token refresh returned no token");
            }
            const refreshError = new Error("Token refresh failed");
            processQueue(refreshError, null);
            if (!USE_COOKIES) {
              authService.removeTokens();
            }
            isRefreshing = false;
            reject(refreshError);

            // Redirect to login only on protected pages, stay on public pages
            if (!isPublicPage()) {
              setTimeout(() => {
                window.location.href = "/login";
              }, 100);
            }
            return;
          }

          // Refresh succeeded - update token and retry request
          if (!USE_COOKIES && newToken !== "cookie") {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          // Process queued requests with new token
          processQueue(null, newToken);

          // Retry original request
          resolve(api(originalRequest));
        })
        .catch((err) => {
          // Refresh failed with error
          // Only log on protected pages (expected on public pages for non-logged-in users)
          if (!isPublicPage()) {
            console.error("[API] Token refresh failed:", err);
          }
          processQueue(err, null);
          if (!USE_COOKIES) {
            authService.removeTokens();
          }
          isRefreshing = false;
          reject(err);

          // Redirect to login only on protected pages, stay on public pages
          if (!isPublicPage()) {
            setTimeout(() => {
              window.location.href = "/login";
            }, 100);
          }
        })
        .finally(() => {
          // Reset refresh flag
          isRefreshing = false;
        });
    });
  }
);

// =============================================================================
// Profession Management API (Admin)
// =============================================================================

/**
 * Get profession review queue items
 * @param {string} status - Filter by status: "pending", "approved", "rejected", or "all"
 * @param {number} limit - Maximum items to return (default: 20)
 * @param {number} skip - Skip for pagination (default: 0)
 */
export const getReviewQueue = async (status = "pending", limit = 20, skip = 0) => {
  const params = new URLSearchParams({ limit, skip });
  // Always send the status, INCLUDING "all". The backend treats "all" as
  // "no status filter" (pending + approved + rejected). We previously omitted
  // the param for "all", but the backend defaults a MISSING status to
  // pending-only — so the "All" filter silently showed only pending items
  // (and looked empty whenever there were no pending rows).
  if (status) {
    params.append("status", status);
  }
  const response = await api.get(`/admin/profession-review-queue?${params}`);
  return response.data;
};

/**
 * Get a specific review queue item with details
 * @param {number} reviewId - The review queue item ID
 */
export const getReviewItem = async (reviewId) => {
  const response = await api.get(`/admin/profession-review-queue/${reviewId}`);
  return response.data;
};

/**
 * Approve a profession review and assign profession to user
 * @param {number} reviewId - The review queue item ID
 * @param {string} professionKey - The profession key to assign
 * @param {string} adminNotes - Optional notes from admin
 */
export const approveReview = async (reviewId, professionKey, adminNotes = null) => {
  const response = await api.post(`/admin/profession-review-queue/${reviewId}/approve`, {
    profession_key: professionKey,
    admin_notes: adminNotes,
  });
  return response.data;
};

/**
 * Reject a profession review.
 * @param {number} reviewId - The review queue item ID
 * @param {string|null} adminNotes - Optional admin comment (stored for audit)
 * @param {Object} [options]
 * @param {boolean} [options.notifyUser] - Send a rejection/correction email (default OFF)
 * @param {boolean} [options.includeAdminNotes] - Include the admin comment in that email (default OFF)
 */
export const rejectReview = async (reviewId, adminNotes, options = {}) => {
  const response = await api.post(`/admin/profession-review-queue/${reviewId}/reject`, {
    admin_notes: adminNotes || null,
    notify_user: !!options.notifyUser,
    include_admin_notes: !!options.includeAdminNotes,
  });
  return response.data;
};

/**
 * Approve a profession review by creating a NEW profession atomically.
 *
 * Server creates the profession, optionally creates a keyword, assigns
 * the new profession_key to the user, and marks the review approved —
 * all in one transaction. Use this when the admin's resolution is to
 * create a brand-new profession (vs matching an existing one, which
 * uses ``approveReview`` instead).
 *
 * @param {number} reviewId - Review queue item ID.
 * @param {Object} payload  - Request body.
 * @param {Object} payload.new_profession - Required spec for the new profession.
 * @param {string} payload.new_profession.profession_key - lowercase a-z0-9_.
 * @param {string} payload.new_profession.hebrew         - Hebrew name (single profession; never combined).
 * @param {string} payload.new_profession.english        - English name.
 * @param {string} payload.new_profession.category       - Free-text; server normalizes synonyms.
 * @param {string} [payload.new_profession.description]  - Optional, max 500 chars.
 * @param {string} [payload.keyword]      - Optional Hebrew search keyword (max 200 chars).
 * @param {string} [payload.admin_notes]  - Optional notes recorded on the review row.
 * @returns {Promise<Object>} server response with profession_id, profession_key, etc.
 */
export const approveReviewWithNewProfession = async (reviewId, payload) => {
  const response = await api.post(
    `/admin/profession-review-queue/${reviewId}/approve-with-new-profession`,
    payload
  );
  return response.data;
};

/**
 * Get profession usage statistics
 */
export const getProfessionUsageStats = async () => {
  const response = await api.get(`/admin/profession-usage-stats`);
  return response.data;
};

// =============================================================================
// New-user onboarding state
// =============================================================================

/**
 * Stamp one or both onboarding "seen" timestamps on the current user.
 *
 * The backend does ONE `UPDATE ... COALESCE(col, now()) ... RETURNING`, so the
 * FIRST stamp wins and the call is idempotent: closing the welcome dialog five
 * times records the first close, not the fifth. At least one flag must be true
 * (422 otherwise), because a request that asks for nothing is a bug at the call
 * site rather than a no-op worth serving.
 *
 * Sibling of `markOnboardingGuideSeen` (services/users.js), which stamps the
 * tour's own monotonic version. The two are deliberately SEPARATE columns:
 * overloading the guide version would re-couple the welcome dialog and the tour,
 * which is exactly the queue this layer untangles.
 *
 * @param {{welcomeSeen?: boolean, firstStepsDismissed?: boolean}} flags
 * @returns {Promise<{onboarding_welcome_seen_at: string|null,
 *   onboarding_first_steps_dismissed_at: string|null}>}
 */
export const markOnboardingState = async ({
  welcomeSeen = false,
  firstStepsDismissed = false,
} = {}) => {
  const response = await api.post("/users/me/onboarding-state", {
    welcome_seen: welcomeSeen,
    first_steps_dismissed: firstStepsDismissed,
  });
  return response.data;
};

// =============================================================================
// User Profession Matching API
// =============================================================================

/**
 * Submit profession description for AI matching (user-facing)
 * @param {string} description - User's profession description
 * @returns {Promise<Object>} - Matching result with status and profession
 */
export const submitProfessionMatching = async (description) => {
  const response = await api.post("/users/me/profession-matching", {
    profession_description_for_matching: description,
  });
  return response.data;
};

/**
 * Submit additional profession for upgraded users (Professional tier)
 * @param {string} description - Additional profession description
 * @returns {Promise<Object>} - Matching result with status and profession
 */
export const submitAdditionalProfession = async (description) => {
  const response = await api.post("/users/me/profession-matching/additional", {
    profession_description_for_matching: description,
  });
  return response.data;
};

// =============================================================================
// Per-profession business descriptions (user-facing)
// =============================================================================
//
// OPT-IN feature for the rare user whose professions are fundamentally
// different (a lawyer who is also a photographer). The general
// `professional_description` on the profile remains the default and covers
// every profession that has no dedicated description here.
//
// These endpoints are DELIBERATELY separate from `PUT /users/me`: the profile
// save carries a whole-object snapshot and a stale one once wiped a customer's
// admin-set categories (see pages/profileUpdatePayload.js). Dedicated
// descriptions must never ride along on that payload.

/**
 * Fetch the user's dedicated per-profession business descriptions.
 *
 * @returns {Promise<Object>} {
 *   items: [{ id, description, profession_keys, created_at, updated_at }],
 *   coverage: [{ profession_key, hebrew, description_id }] - description_id
 *             null means the profession falls back to the general description,
 *   max_items: number,
 *   general_description: string|null
 * }
 *
 * Open to EVERY authenticated user regardless of profession count. A user who
 * drops back to a single profession must still be able to see and delete rows
 * they created earlier - the backend keeps serving those rows for leads in the
 * old category until they do.
 */
export const getProfessionDescriptions = async () => {
  const response = await api.get("/users/me/profession-descriptions");
  return response.data;
};

/**
 * Create a dedicated description covering one or more professions.
 *
 * @param {Object} data - { description: string, profession_keys: string[] }
 * @returns {Promise<Object>} the created item
 * @throws 409 { detail: { code: "profession_already_covered", profession_keys } }
 */
export const createProfessionDescription = async (data) => {
  const response = await api.post("/users/me/profession-descriptions", data);
  return response.data;
};

/**
 * Update a dedicated description (partial - send only what changed).
 *
 * @param {string} descriptionId - Description UUID
 * @param {Object} data - { description?: string, profession_keys?: string[] }
 * @returns {Promise<Object>} the updated item
 * @throws 409 { detail: { code: "profession_already_covered", profession_keys } }
 */
export const updateProfessionDescription = async (descriptionId, data) => {
  const response = await api.patch(
    `/users/me/profession-descriptions/${descriptionId}`,
    data
  );
  return response.data;
};

/**
 * Delete a dedicated description. Its professions fall back to the general
 * business description. Responds 204 with no body.
 *
 * @param {string} descriptionId - Description UUID
 * @returns {Promise<void>}
 */
export const deleteProfessionDescription = async (descriptionId) => {
  await api.delete(`/users/me/profession-descriptions/${descriptionId}`);
};

// =============================================================================
// Facebook Groups API (User-facing)
// =============================================================================

/**
 * Get user's Facebook groups with tier limits
 * @returns {Promise<Object>} - { groups: [], limits: { max_groups, current_count, remaining_slots } }
 */
export const getGroups = async () => {
  const response = await api.get("/groups");
  return response.data;
};

/**
 * Create a new Facebook group
 * @param {Object} data - { name: string, url: string, description?: string }
 * @returns {Promise<Object>} - Created group
 */
export const createGroup = async (data) => {
  const response = await api.post("/groups", data);
  return response.data;
};

/**
 * Update a Facebook group (name and description only)
 * @param {string} groupId - Group UUID
 * @param {Object} data - { name?: string, description?: string }
 * @returns {Promise<Object>} - Updated group
 */
export const updateGroup = async (groupId, data) => {
  const response = await api.patch(`/groups/${groupId}`, data);
  return response.data;
};

/**
 * Delete a Facebook group (soft delete)
 * @param {string} groupId - Group UUID
 * @returns {Promise<Object>} - { success, message }
 */
export const deleteGroup = async (groupId) => {
  const response = await api.delete(`/groups/${groupId}`);
  return response.data;
};

// Groups API object for convenience
export const groupsAPI = {
  list: getGroups,
  create: createGroup,
  update: updateGroup,
  delete: deleteGroup,
};

export default api;
